var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PackingTreeView
 * Unified tree for sidebar — merges packages and containers.
 *
 * Layout:
 *   Packages
 *     > Package #00001   [Closed]
 *       1 item · MD
 *   Containers (shown if containers exist)
 *     v Pallet #C-001     [Open]
 *         Carton #C-002   [Open]
 */
import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Menu } from 'antd';
import { DeleteOutlined, SearchOutlined, CheckOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { ContainerNode } from './ContainerNode';
/** Styled status badge */
var StatusBadge = function (_a) {
    var label = _a.label, tone = _a.tone, isSelected = _a.isSelected;
    var base = 'rounded px-1.5 py-0.5 font-semibold flex-shrink-0 whitespace-nowrap';
    var toneClasses = isSelected
        ? { open: 'bg-blue-500 text-white', closed: 'bg-blue-400 text-blue-100', pending: 'bg-orange-50 text-orange-600' }
        : { open: 'bg-blue-50 text-blue-600', closed: 'bg-green-50 text-green-600', pending: 'bg-orange-50 text-orange-600' };
    return (_jsx("span", __assign({ className: base + " " + toneClasses[tone], style: { fontSize: 10, lineHeight: '16px' } }, { children: label }), void 0));
};
/** Styled context menu overlay to apply style */
var styledMenu = function (items) { return (_jsx(Menu, __assign({ className: 'font-inter', style: {
        fontSize: 12,
        minWidth: 160,
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        padding: '4px 0'
    } }, { children: items }), void 0)); };
/** Package row item */
var PackageRow = function (_a) {
    var item = _a.item, isSelected = _a.isSelected, onSelect = _a.onSelect;
    var t = useTranslation().t;
    var statusTone = item.kind === 'virtual' ? 'pending' : item.isClosed ? 'closed' : 'open';
    var statusLabel = item.kind === 'virtual' ? t('Pending') : item.isClosed ? t('Closed') : t('Open');
    return (_jsxs("div", __assign({ className: "\n        flex items-start gap-2 cursor-pointer select-none\n        rounded-md transition-colors\n        " + (isSelected ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-800 hover:bg-gray-100') + "\n      ", style: { padding: '6px 8px', marginTop: 4, border: '1px solid #e5e7eb', borderRadius: 6 }, onClick: onSelect }, { children: [_jsx("span", { className: 'flex-shrink-0 w-4' }, void 0), _jsxs("div", __assign({ className: 'flex-1 min-w-0' }, { children: [_jsxs("div", __assign({ className: 'flex items-center gap-1.5 justify-between' }, { children: [_jsx("span", __assign({ className: 'font-semibold truncate', style: { fontSize: 13 } }, { children: item.kind === 'virtual' ? item.displayId : "Package " + item.displayId }), void 0), _jsx(StatusBadge, { label: statusLabel, tone: statusTone, isSelected: isSelected }, void 0)] }), void 0), _jsx("div", __assign({ className: 'flex items-center gap-1.5 mt-0.5' }, { children: _jsx("span", __assign({ className: "" + (isSelected ? 'text-blue-200' : 'text-gray-400'), style: { fontSize: 11 } }, { children: item.itemCount > 0 ? item.itemCount + " " + t('items') : t('Empty') }), void 0) }), void 0)] }), void 0)] }), void 0));
};
export var PackingTreeView = function (_a) {
    var packageItems = _a.packageItems, containerList = _a.containerList, containerLinkedPackageSeqIds = _a.containerLinkedPackageSeqIds, containerTypeMap = _a.containerTypeMap, selectedPackageKey = _a.selectedPackageKey, selectedContainerKey = _a.selectedContainerKey, onPackageSelect = _a.onPackageSelect, onContainerSelect = _a.onContainerSelect, onContainerClose = _a.onContainerClose, onContainerReopen = _a.onContainerReopen, onContainerDelete = _a.onContainerDelete, onContainerReset = _a.onContainerReset, onBulkPack = _a.onBulkPack, onDeleteVirtualPackage = _a.onDeleteVirtualPackage;
    var t = useTranslation().t;
    var listRef = useRef(null);
    var _b = useState(''), searchTerm = _b[0], setSearchTerm = _b[1];
    var rootContainers = useMemo(function () { return containerList.filter(function (c) { return c.isRoot; }); }, [containerList]);
    // Filter out packages linked to containers — they managed through the container
    var standalonePackages = useMemo(function () {
        return packageItems.filter(function (pkg) {
            if (pkg.kind === 'real' &&
                pkg.shipmentPackageSeqId &&
                containerLinkedPackageSeqIds.has(pkg.shipmentPackageSeqId)) {
                return false;
            }
            return true;
        });
    }, [packageItems, containerLinkedPackageSeqIds]);
    var handleContainerContextAction = useCallback(function (containerId, action) {
        switch (action) {
            case 'close':
                onContainerClose === null || onContainerClose === void 0 ? void 0 : onContainerClose(containerId);
                break;
            case 'reopen':
                onContainerReopen === null || onContainerReopen === void 0 ? void 0 : onContainerReopen(containerId);
                break;
            case 'delete':
                onContainerDelete === null || onContainerDelete === void 0 ? void 0 : onContainerDelete(containerId);
                break;
            case 'reset':
                onContainerReset === null || onContainerReset === void 0 ? void 0 : onContainerReset(containerId);
                break;
        }
    }, [onContainerClose, onContainerReopen, onContainerDelete, onContainerReset]);
    // Auto-scroll to selected
    useEffect(function () {
        if (!listRef.current)
            return;
        var timer = window.setTimeout(function () {
            var _a;
            var el = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[data-selected="true"]');
            el === null || el === void 0 ? void 0 : el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 50);
        return function () { return window.clearTimeout(timer); };
    }, [selectedPackageKey, selectedContainerKey]);
    var hasPackages = standalonePackages.length > 0;
    var hasContainers = rootContainers.length > 0;
    // Search filtering, match package seq id, container id, box type and packer
    var trimmedSearch = searchTerm.trim().toLowerCase();
    var isSearching = trimmedSearch.length > 0;
    var filteredPackages = useMemo(function () {
        if (!isSearching)
            return standalonePackages;
        return standalonePackages.filter(function (pkg) {
            if (pkg.displayId.toLowerCase().includes(trimmedSearch))
                return true;
            if ((pkg.shipmentPackageSeqId || '').toLowerCase().includes(trimmedSearch))
                return true;
            if ((pkg.boxTypeDescription || '').toLowerCase().includes(trimmedSearch))
                return true;
            return false;
        });
    }, [standalonePackages, isSearching, trimmedSearch]);
    var filteredRootContainers = useMemo(function () {
        if (!isSearching)
            return rootContainers;
        return rootContainers.filter(function (c) {
            if (c.containerId.toLowerCase().includes(trimmedSearch))
                return true;
            if ((c.serialNumber || '').toLowerCase().includes(trimmedSearch))
                return true;
            if ((c.containerTypeEnumId || '').toLowerCase().includes(trimmedSearch))
                return true;
            // Also match the resolved type name
            var typeName = c.containerTypeEnumId ? containerTypeMap.get(c.containerTypeEnumId) : null;
            if (typeName && typeName.toLowerCase().includes(trimmedSearch))
                return true;
            return false;
        });
    }, [rootContainers, isSearching, trimmedSearch, containerTypeMap]);
    var hasFilteredPackages = filteredPackages.length > 0;
    var hasFilteredContainers = filteredRootContainers.length > 0;
    // Extract the real containerId from selectedContainerKey for ContainerNode selection
    var selectedRealContainerId = useMemo(function () {
        if (!selectedContainerKey)
            return null;
        if (selectedContainerKey.startsWith('real:'))
            return selectedContainerKey.slice(5);
        return null;
    }, [selectedContainerKey]);
    if (!hasPackages && !hasContainers) {
        return (_jsx("div", __assign({ className: 'flex items-center justify-center h-full p-4 text-gray-400 text-sm' }, { children: t('No packages or containers') }), void 0));
    }
    return (_jsxs("div", __assign({ className: 'flex flex-col min-h-0 h-full' }, { children: [_jsx("div", __assign({ className: 'px-2 pt-2 pb-1 flex-shrink-0' }, { children: _jsxs("div", __assign({ className: 'relative' }, { children: [_jsx(SearchOutlined, { className: 'pointer-events-none absolute text-gray-400', style: { left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12 } }, void 0), _jsx("input", { type: 'text', value: searchTerm, onChange: function (e) { return setSearchTerm(e.target.value); }, placeholder: t('Search packages / containers...'), className: 'w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pr-3 text-sm text-gray-700 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none', style: { paddingLeft: 30, fontSize: 12 } }, void 0)] }), void 0) }), void 0), _jsxs("div", __assign({ ref: listRef, className: 'flex flex-col py-1 px-2 overflow-y-auto min-h-0 flex-1' }, { children: [hasFilteredPackages && (filteredPackages.map(function (pkg) {
                        var isSelected = selectedPackageKey === pkg.key;
                        var isVirtual = pkg.kind === 'virtual';
                        var row = (_jsx(PackageRow, { item: pkg, isSelected: isSelected, onSelect: function () { return onPackageSelect(pkg); } }, void 0));
                        return (_jsx("div", __assign({ "data-selected": isSelected || undefined }, { children: _jsx(Dropdown, __assign({ overlay: styledMenu(_jsxs(_Fragment, { children: [_jsx(Menu.Item, __assign({ icon: isSelected ? _jsx(CloseCircleOutlined, {}, void 0) : _jsx(CheckOutlined, {}, void 0), style: { fontSize: 12 }, onClick: function () { return onPackageSelect(pkg); } }, { children: isSelected ? t('Deselect') : t('Select') }), 'select'), isVirtual && onDeleteVirtualPackage && (_jsx(Menu.Item, __assign({ danger: true, icon: _jsx(DeleteOutlined, {}, void 0), style: { fontSize: 12 }, onClick: function () { return onDeleteVirtualPackage(pkg.virtualTempId); } }, { children: t('Delete') }), 'delete'))] }, void 0)), trigger: ['contextMenu'] }, { children: _jsx("div", { children: row }, void 0) }), void 0) }), pkg.key));
                    })), hasFilteredContainers && (_jsx(_Fragment, { children: filteredRootContainers.map(function (container) {
                            var isSelected = selectedRealContainerId === container.containerId;
                            return (_jsx("div", __assign({ "data-selected": isSelected || undefined }, { children: _jsx(ContainerNode, { container: container, allContainers: containerList, containerTypeMap: containerTypeMap, selectedContainerId: selectedRealContainerId, depth: 1, onSelect: function (containerId) { return onContainerSelect({ kind: 'real', containerId: containerId }); }, onContextAction: handleContainerContextAction, onBulkPack: onBulkPack }, void 0) }), container.containerId));
                        }) }, void 0))] }), void 0)] }), void 0));
};
export default PackingTreeView;
